import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import BulkUploadModal from 'components/molecules/PricingSimulator/Modals/BulkUploadModal';
import {
    UPLOAD_POLLING_INTERVAL,
    UploadPollingStatus,
} from 'components/molecules/PricingSimulator/ScenarioActions';
import { pricingSimulatorDataContext } from 'contexts/PricingSimulatorContext';
import { PricingSimulatorWrapperContext } from 'contexts/PricingSimulatorWrapperContext';
import { pricingSimulatorUrls } from 'config/urls';
import { getAuthUserMail } from 'utils/helperMethods';
import type { BulkUploadInfo, Project, Scenario } from 'types/pricingSimulator';

type BulkUploadModalBridgeProps = {
    open: boolean;
    onClose: () => void;
    scenarioId: number;
    projectId: number;
    projectName: string;
    scenarioName: string;
    createdBy?: string;
    onUploadSuccess?: () => void;
};

const PricingSimulatorContextBridge: FC<BulkUploadModalBridgeProps> = ({
    open,
    onClose,
    scenarioId,
    projectId,
    projectName,
    scenarioName,
    createdBy = '',
    onUploadSuccess,
}) => {
    const parentContext = useContext(pricingSimulatorDataContext);
    const loggedInUser = getAuthUserMail() ?? '';
    const [selectedScenarioInfo, setSelectedScenarioInfo] = useState<Scenario | null>(null);
    const [selectedProjectInfo, setSelectedProjectInfo] = useState<Project | null>(null);
    const [pollingStatus, setPollingStatus] = useState<UploadPollingStatus | null>(null);

    useEffect(() => {
        if (!open) {
            setSelectedScenarioInfo(null);
            setSelectedProjectInfo(null);
            setPollingStatus(null);
            return;
        }

        setSelectedScenarioInfo({
            scenarioId,
            projectId,
            scenarioName,
            createdBy,
            updatedAt: '',
            status: 'SETUP',
        });
        setSelectedProjectInfo({
            projectId,
            projectName,
            createdBy,
            lastUpdated: '',
            dataSource: '',
            dim1Filter: null,
            dim2Filter: null,
            status: 'SETUP',
            simulationPeriod: '',
            priceEffectiveDate: '',
        });
    }, [createdBy, open, projectId, projectName, scenarioId, scenarioName]);

    const updateScenarioProperty = useCallback(<K extends keyof Scenario>(key: K, value: Scenario[K]) => {
        setSelectedScenarioInfo((current) => (current ? { ...current, [key]: value } : current));
    }, []);

    const pollingFn = async () => {
        try {
            const data = await axios.get(`${pricingSimulatorUrls.getUploadStatus}/${scenarioId}`);
            setPollingStatus(data.data.bulk_upload_status);
            const newBulkUploadInfo: BulkUploadInfo = {
                status: data.data?.bulk_upload_status,
                errorObject: data?.data?.upload_error_message,
            };
            if (data.data?.bulk_upload_status === UploadPollingStatus.SUCCESS) {
                onUploadSuccess?.();
            }
            updateScenarioProperty('bulkUploadInfo', newBulkUploadInfo);
        } catch (err) {
            const newBulkUploadInfo: BulkUploadInfo = {
                status: UploadPollingStatus.ERROR,
                errorObject: {},
            };
            setPollingStatus(UploadPollingStatus.ERROR);
            updateScenarioProperty('bulkUploadInfo', newBulkUploadInfo);
            console.error(err);
        }
    };

    useQuery({
        queryKey: [`get_upload_status/${scenarioId}`],
        queryFn: pollingFn,
        refetchInterval: pollingStatus === UploadPollingStatus.IN_PROGRESS ? UPLOAD_POLLING_INTERVAL : false,
        enabled: open && scenarioId > 0,
        staleTime: 0,
    });

    const contextValue = useMemo(
        () => ({
            ...parentContext,
            projectId,
            scenarioId,
            selectedScenarioInfo,
            selectedProjectInfo,
            loggedInUser,
            updateScenarioProperty,
        }),
        [
            loggedInUser,
            parentContext,
            projectId,
            scenarioId,
            selectedProjectInfo,
            selectedScenarioInfo,
            updateScenarioProperty,
        ],
    );

    return (
        <pricingSimulatorDataContext.Provider value={contextValue}>
            <BulkUploadModal
                open={open}
                onCancel={onClose}
                onSubmitFinish={onClose}
                setPollingStatus={setPollingStatus}
            />
        </pricingSimulatorDataContext.Provider>
    );
};

const BulkUploadModalBridge: FC<BulkUploadModalBridgeProps> = (props) => (
    <PricingSimulatorWrapperContext>
        <PricingSimulatorContextBridge {...props} />
    </PricingSimulatorWrapperContext>
);

export default BulkUploadModalBridge;
